import { useCallback, useState, useEffect } from 'react';
import { Box, Modal, Table, Text } from '@bigcommerce/big-design';
import { useClient } from '../../../../context/client';
import './ModalConfirmation.css';
import OrderStatusColumn from './OrderStatusColumn';

const tableColumns = [
	{
		header: 'Order Number',
		width: '100px',
		hash: 'bc_order_id',
		render: ({ bc_order_id }) => bc_order_id,
	},
	{
		header: 'Old status',
		hash: 'status',
		render: ({ status }) => <OrderStatusColumn status={status} />,
	},
	{
		render: () => <Text>&#8594;</Text>,
	},
	{
		header: 'New Status',
		hash: 'new_status',
		render: ({ new_status }) => <OrderStatusColumn status={new_status} />,
	},
];

function useUpdateOrderStatus() {
	const [isLoadingStatus, setIsLoadingStatus] = useState(false);
	const client = useClient();

	const dispatch = useCallback(
		async (orderIds, status) => {
			setIsLoadingStatus(true);

			try {
				await client.updateOrderStatus(orderIds, status);
			} finally {
				setIsLoadingStatus(false);
			}
		},
		[client],
	);

	return [isLoadingStatus, dispatch];
}

export default function ModalConfirmation({
	items,
	isOpen,
	setIsOpen,
	statusOptionSelected,
	fetchOrdersList,
}) {
	const [selectedItems, setSelectedItems] = useState(items);
	const [isLoadingStatus, updateOrderStatus] = useUpdateOrderStatus();
	useEffect(() => setSelectedItems(items), [items]);

	const checkSimilarItems = items.map((item) =>
		Boolean(item.status === item.new_status),
	);

	if (checkSimilarItems.every((item) => item === true)) {
		return (
			<Modal
				actions={[
					{
						text: 'Close',
						onClick: () => setIsOpen(false),
					},
				]}
				closeOnClickOutside={false}
				closeOnEscKey={true}
				header="Nothing to update"
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
			>
				<Box className="modal-confirmation-text">
					<Text margin="xxxLarge">
						All the orders you selected are already set to the{' '}
						<strong>{statusOptionSelected}</strong> status.
					</Text>
				</Box>
			</Modal>
		);
	}
	return (
		<Modal
			actions={[
				{
					text: 'Cancel',
					variant: 'subtle',
					onClick: () => setIsOpen(false),
				},
				{
					text: 'Confirm',
					isLoading: isLoadingStatus,
					onClick: async () => {
						await updateOrderStatus(
							selectedItems.map(({ bc_order_id }) => bc_order_id),
							statusOptionSelected,
						);

						setIsOpen(false);
						fetchOrdersList();
					},
				},
			]}
			closeOnClickOutside={false}
			closeOnEscKey={true}
			header="Confirm Status Updates"
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
		>
			<Table
				columns={tableColumns}
				items={items}
				selectable={{
					selectedItems,
					onSelectionChange: setSelectedItems,
				}}
				stickyHeader
			/>
			<Box className="modal-confirmation-text">
				<Text margin="xxxLarge">
					Update the status of {selectedItems.length} out of{' '}
					{items.length} selected orders.
				</Text>
			</Box>
		</Modal>
	);
}
